import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Storefront, Sparkle, ArrowRight, QrCode, Fingerprint } from "@phosphor-icons/react";
import { Wordmark } from "../components/Logo";
import { api, setMerchantId, setMerchantName } from "../lib/api";

const features = [
  { icon: Fingerprint, title: "Immutable Provenance", desc: "Every item carries a cryptographically hashed passport that can never be altered." },
  { icon: QrCode, title: "Public QR Verification", desc: "Customers scan and instantly verify authenticity, ownership and full history." },
  { icon: ShieldCheck, title: "Ownership Transfers", desc: "Securely transfer ownership to buyers with a full audit trail." },
];

export default function Install() {
  const navigate = useNavigate();
  const [shop, setShop] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const connect = () => {
    if (!shop.trim()) return toast.error("Enter your Shopline store domain");
    const domain = shop.trim().replace(/^https?:\/\//, "");
    window.location.href = `${api.defaults.baseURL}/auth/shopline?shop=${encodeURIComponent(domain)}`;
  };

  const launchDemo = async () => {
    setDemoLoading(true);
    try {
      const { data } = await api.post("/demo/seed");
      setMerchantId(data.merchant_id);
      setMerchantName(data.merchant_name);
      toast.success("Demo workspace ready");
      navigate("/dashboard");
    } catch (e) {
      toast.error("Could not launch demo");
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy text-cream relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[32rem] h-[32rem] rounded-full" style={{ background: "radial-gradient(circle, rgba(201,168,76,0.18), transparent 70%)" }} />
      <div className="absolute -bottom-52 -left-40 w-[36rem] h-[36rem] rounded-full" style={{ background: "radial-gradient(circle, rgba(201,168,76,0.10), transparent 70%)" }} />

      <header className="relative z-10 flex items-center justify-between px-6 sm:px-12 py-7">
        <Wordmark light />
        <span className="text-[0.62rem] uppercase tracking-[0.25em] text-cream/50">by Valura Technologies</span>
      </header>

      <div className="relative z-10 grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto px-6 sm:px-12 py-12 lg:py-20">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.25em] text-gold border border-gold/30 rounded-full px-4 py-1.5">
            <Sparkle size={13} weight="fill" /> Digital Product Passport Platform
          </span>
          <h1 className="mt-7 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Authenticity, made <span className="text-gradient-gold">permanent</span>.
          </h1>
          <p className="mt-6 text-cream/70 text-base leading-relaxed max-w-md">
            Vcoin issues a tamper-proof Digital Product Passport for every luxury item in your Shopline store — complete with a unique QR code and a public verification page your customers will trust.
          </p>

          <div className="mt-10 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 max-w-md">
            <label className="text-[0.62rem] uppercase tracking-[0.2em] text-cream/50">Connect your store</label>
            <div className="mt-3 flex items-center gap-2 bg-navy/40 rounded-xl border border-white/10 px-4 focus-within:border-gold/50 transition-colors">
              <Storefront size={20} weight="light" className="text-gold" />
              <input
                data-testid="shop-domain-input"
                value={shop}
                onChange={(e) => setShop(e.target.value)}
                placeholder="your-store.myshopline.com"
                className="flex-1 bg-transparent py-3.5 text-sm outline-none placeholder:text-cream/30 text-cream"
              />
            </div>
            <button
              data-testid="install-shopline-button"
              onClick={connect}
              disabled={loading}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-gold text-navy font-medium rounded-xl py-3.5 text-sm hover:brightness-105 active:scale-[0.99] transition-all disabled:opacity-60"
            >
              Install from Shopline <ArrowRight size={17} weight="bold" />
            </button>
            <button
              data-testid="launch-demo-button"
              onClick={launchDemo}
              disabled={demoLoading}
              className="mt-2 w-full text-sm text-cream/60 hover:text-gold py-2 transition-colors disabled:opacity-60"
            >
              {demoLoading ? "Preparing…" : "Or explore the demo workspace →"}
            </button>
          </div>
        </div>

        <div className="space-y-4 animate-fade-up" style={{ animationDelay: "0.15s" }}>
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="flex gap-4 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:-translate-y-1 transition-transform duration-300">
                <div className="shrink-0 h-11 w-11 rounded-xl bg-gold/15 flex items-center justify-center">
                  <Icon size={22} weight="light" className="text-gold" />
                </div>
                <div>
                  <h3 className="text-lg text-cream">{f.title}</h3>
                  <p className="text-sm text-cream/60 mt-1 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
